import { useEffect, useState } from 'react';
import { isProblemStatus } from '@/services/api';
import { nomenclatureApi } from '@/services/nomenclatureApi';
import type { NomenclatureItem } from '@/features/common/types/NomenclatureItem';

export type { NomenclatureItem };

// One request per code and per page load, shared: the category field and an article's categories
// offer the same nomenclature, each modal opening mounts them again, and every one of those used to
// be its own GET. The answer is kept only for the life of the page, so a nomenclature edited in
// "Configurare sistem" is read again on the next load.
const asked = new Map<string, Promise<NomenclatureItem[]>>();

/** Every mounted picker, so that forgetting a code re-reads the lists already on screen. */
const watchers = new Set<() => void>();

/**
 * Throws away one nomenclature's cached answer and has every picker reading it ask again.
 *
 * The cache above is "one request per code and per page load", which is what a tab left open all
 * morning holds: the list it read then. That is precisely the list the server now refuses to
 * write from — an item retired since is no longer offered, and T242 turns choosing one into a
 * 422/409. Without this the reader is refused, looks at the picker, sees the option still there,
 * and has no way to understand the answer.
 */
export function forgetNomenclature(code: string): void {
  asked.delete(code);
  watchers.forEach((reread) => reread());
}

/**
 * The same, after a save the server refused.
 *
 * Any 422 or 409 naming items is read as "the vocabulary you offered is out of date", not only
 * the "… retrase" sentence: an id reported as unknown is equally a list this page has held too
 * long, and the two refusals differ only in how the item left. Keying on the wording would tie
 * these screens to a Romanian sentence the server composes, and there is no problem `type` to
 * key on instead — both come back as a bare status. Re-reading one small cached list costs a
 * GET, and only on a refusal.
 */
export function forgetNomenclatureOnRefusal(error: unknown, code: string): void {
  if (isProblemStatus(error, 422, 409)) {
    forgetNomenclature(code);
  }
}

/**
 * That same request as a promise, for a caller that needs the list rather than state — the category
 * vocabulary reads two nomenclatures and shows neither until both are there.
 */
export function nomenclatureItemsOf(code: string): Promise<NomenclatureItem[]> {
  const pending =
    asked.get(code) ??
    nomenclatureApi.publicItems<NomenclatureItem>(code).catch(() => {
      // Not kept: a failed load is asked again by whoever mounts next, rather than remembered
      // as an empty nomenclature for the rest of the page's life.
      asked.delete(code);
      return [] as NomenclatureItem[];
    });
  asked.set(code, pending);
  return pending;
}

/**
 * The items of one administered nomenclature, by its code.
 *
 * Extracted from `features/library/useThematicDomains`, which had exactly this body with
 * `LIBRARY_DOMENII` baked into the URL. Four features already read a nomenclature that is not
 * the library's — the training screens and the funding screens import `domainLabel` from the
 * library module — so the next caller either copied the fetch or borrowed a hook named after
 * someone else's vocabulary. Parameterising the code is what stops a third copy of these ten
 * lines from being written.
 *
 * Read from the nomenclature rather than hardcoded, which is the whole point: FR-042 requires
 * these to be administrable without a code change, and "Configurare sistem" can rename, reorder
 * and retire them. A list baked into the bundle would put that back.
 *
 * Served by the public endpoint, so an anonymous page can use it too. A failure yields an empty
 * list: a missing picker is a smaller problem than a page that will not render.
 */
export function useNomenclatureItems(code: string): NomenclatureItem[] {
  const [items, setItems] = useState<NomenclatureItem[]>([]);
  // Bumped by forgetNomenclature, which is what re-runs the read below on a list already drawn.
  const [generation, setGeneration] = useState(0);

  useEffect(() => {
    const reread = () => setGeneration((n) => n + 1);
    watchers.add(reread);
    return () => {
      watchers.delete(reread);
    };
  }, []);

  useEffect(() => {
    let live = true;
    void nomenclatureItemsOf(code).then((list) => {
      if (live) setItems(list);
    });
    return () => {
      live = false;
    };
  }, [code, generation]);

  return items;
}
